import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { useContent, LOGIN_DEFAULTS } from "@/lib/content";
import { Car } from "lucide-react";
import { toast } from "sonner";

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const c = useContent("login", LOGIN_DEFAULTS);
  const slides = [1, 2, 3, 4, 5].filter((i) => c[`login_image_${i}`]);
  const seed = useMemo(() => Math.random(), []);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (slides.length < 2) return;
    const id = setInterval(() => setIdx((i) => i + 1), 6000);
    return () => clearInterval(id);
  }, [slides.length]);
  const n = slides.length ? slides[(Math.floor(seed * slides.length) + idx) % slides.length] : 1;
  const hero = c[`login_image_${n}`] || LOGIN_DEFAULTS.login_image_1;
  const tagline = c[`login_tagline_${n}`] || c.login_tagline_1 || c.login_tagline || LOGIN_DEFAULTS.login_tagline_1;
  const subtext = c[`login_subtext_${n}`] || c.login_subtext_1 || c.login_subtext || LOGIN_DEFAULTS.login_subtext_1;
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const user = mode === "login"
        ? await login(form.email, form.password)
        : await register(form.email, form.password, form.name);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate(user.role === "partner" ? "/partner" : ["super_admin", "broker", "sales", "staff", "admin"].includes(user.role) ? "/staff" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4 py-12">
        <div className="w-full max-w-sm ab-fade-up">
          <button onClick={() => navigate("/")} className="mb-8 flex items-center gap-2" data-testid="auth-logo">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-950 text-white"><Car className="h-5 w-5" strokeWidth={2.5} /></div>
            <span className="font-display text-xl font-black tracking-tighter text-slate-950 dark:text-slate-50">AutoBroker</span>
          </button>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-950 dark:text-slate-50">{mode === "login" ? c.login_heading : c.register_heading}</h1>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{mode === "login" ? c.login_subheading : c.register_subheading}</p>

          <Button onClick={google} data-testid="google-login-btn" variant="outline" className="mt-6 w-full rounded-md border-slate-300 dark:border-slate-700 py-6 font-semibold">
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </Button>

          <div className="my-5 flex items-center gap-3 text-xs text-slate-400 dark:text-slate-500"><div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" />OR<div className="h-px flex-1 bg-slate-200 dark:bg-slate-800" /></div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Full name</Label>
                <Input data-testid="auth-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" placeholder="Jane Driver" />
              </div>
            )}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Email</Label>
              <Input data-testid="auth-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5" placeholder="you@email.com" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Password</Label>
              <Input data-testid="auth-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1.5" placeholder="••••••••" />
            </div>
            {error && <p data-testid="auth-error" className="text-sm font-medium text-red-600">{error}</p>}
            <Button type="submit" disabled={busy} data-testid="auth-submit-btn" className="w-full rounded-md bg-slate-950 py-6 font-semibold hover:bg-slate-800">
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
            {mode === "login" ? "New here? " : "Already have an account? "}
            <button data-testid="auth-toggle-btn" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="font-semibold text-rose-600 hover:underline">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
      <div className="relative hidden overflow-hidden bg-slate-950 lg:block">
        {slides.map((i) => (
          <img key={i} src={c[`login_image_${i}`]} alt="" aria-hidden={i !== n}
            data-testid={i === n ? "auth-hero-image" : undefined}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1500ms] ease-in-out ${i === n ? "opacity-100" : "opacity-0"}`} />
        ))}
        {slides.length === 0 && <img src={hero} alt="Vehicle" data-testid="auth-hero-image" className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
        <div key={n} className="absolute bottom-10 left-10 right-10 text-white animate-in fade-in slide-in-from-bottom-2 duration-700">
          <p className="font-display text-2xl font-bold" data-testid="auth-hero-tagline">{tagline}</p>
          <p className="mt-2 max-w-md text-sm text-white/80" data-testid="auth-hero-subtext">{subtext}</p>
          {slides.length > 1 && (
            <div className="mt-5 flex gap-1.5" data-testid="auth-slide-dots">
              {slides.map((i) => <span key={i} className={`h-1 rounded-full transition-all duration-500 ${i === n ? "w-6 bg-rose-500" : "w-2 bg-white/40"}`} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
